import React, { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { UIBlockRenderer, UIBlockData } from './UIBlockRenderer';

const PreviewContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  z-index: 2000;
`;

const PreviewHeader = styled.div`
  background: #1f2937;
  color: white;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2001;
`;

const PreviewTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const PreviewControls = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const DeviceSelector = styled.div`
  display: flex;
  gap: 8px;
  background: #374151;
  padding: 4px;
  border-radius: 6px;
`;

const DeviceButton = styled.button<{ active: boolean }>`
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  background: ${props => props.active ? '#3b82f6' : 'transparent'};
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${props => props.active ? '#3b82f6' : '#4b5563'};
  }
`;

const PreviewActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-size: 14px;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #3b82f6;
          color: white;
          &:hover {
            background: #2563eb;
          }
        `;
      default:
        return `
          background: #6b7280;
          color: white;
          &:hover {
            background: #4b5563;
          }
        `;
    }
  }}
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #374151;
  }
`;

const PreviewBody = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  padding-top: 88px; /* Account for header height */
  overflow: hidden;
`;

const DeviceFrame = styled.div<{ device: DeviceType }>`
  background: white;
  border-radius: ${props => {
    switch (props.device) {
      case 'mobile': return '24px';
      case 'tablet': return '12px';
      default: return '8px';
    }
  }};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
  transition: all 0.3s ease-in-out;
  overflow: hidden;

  ${props => {
    switch (props.device) {
      case 'desktop':
        return `
          width: 1200px;
          height: 80vh;
          min-height: 600px;
        `;
      case 'tablet':
        return `
          width: 768px;
          height: 1024px;
          max-height: 80vh;
        `;
      case 'mobile':
        return `
          width: 375px;
          height: 667px;
          max-height: 80vh;
        `;
    }
  }}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: ${props => props.device === 'mobile' ? '20px' : '0'};
    background: #1f2937;
    border-radius: ${props => props.device === 'mobile' ? '24px 24px 0 0' : '0'};
    z-index: 10;
  }
`;

const PreviewContent = styled.div<{ device: DeviceType }>`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: ${props => props.device === 'mobile' ? '20px' : '0'};
  background: white;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #6b7280;
  font-size: 14px;
  z-index: 5;
`;

const DeviceInfo = styled.div`
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  z-index: 10;
`;

const PreviewBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #3b82f6;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  z-index: 15;
  opacity: 0.9;
`;

const BlocksContainer = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;

type DeviceType = 'desktop' | 'tablet' | 'mobile';

interface Device {
  type: DeviceType;
  name: string;
  icon: string;
  width: number;
  height?: number;
}

const devices: Device[] = [
  { type: 'desktop', name: 'Desktop', icon: '🖥️', width: 1200 },
  { type: 'tablet', name: 'Tablet', icon: '📱', width: 768, height: 1024 },
  { type: 'mobile', name: 'Mobile', icon: '📱', width: 375, height: 667 },
];

interface PageBlock {
  id: string;
  blockId: string;
  overrides: Record<string, any>;
  position: number;
  block: UIBlockData;
  slotData?: Record<string, any>;
}

interface EnhancedResponsivePreviewProps {
  isOpen: boolean;
  pageTitle: string;
  blocks: PageBlock[];
  onClose: () => void;
  onRefresh?: () => void;
  theme?: {
    colors: {
      primary: string;
      secondary: string;
      accent: string;
      background: string;
      text: string;
      border: string;
    };
    fonts: {
      heading: string;
      body: string;
    };
    spacing: {
      padding: string;
      margin: string;
      borderRadius: string;
    };
  };
}

const defaultTheme = {
  colors: {
    primary: '#3b82f6',
    secondary: '#1f2937',
    accent: '#f59e0b',
    background: '#ffffff',
    text: '#374151',
    border: '#e5e7eb',
  },
  fonts: {
    heading: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    body: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  spacing: {
    padding: '2rem',
    margin: '1rem',
    borderRadius: '8px',
  },
};

export const EnhancedResponsivePreview: React.FC<EnhancedResponsivePreviewProps> = ({
  isOpen,
  pageTitle,
  blocks,
  onClose,
  onRefresh,
  theme = defaultTheme,
}) => {
  const [selectedDevice, setSelectedDevice] = useState<DeviceType>('desktop');
  const [isLoading, setIsLoading] = useState(false);

  // Handle ESC key press
  React.useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Handle click outside modal
  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking on the backdrop itself (not on child elements)
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // Sort blocks by position
  const sortedBlocks = useMemo(() => {
    return [...blocks].sort((a, b) => a.position - b.position);
  }, [blocks]);

  const handleDeviceChange = (device: DeviceType) => {
    setSelectedDevice(device);
  };

  const handleRefresh = () => {
    if (onRefresh) {
      setIsLoading(true);
      setTimeout(() => {
        onRefresh();
        setIsLoading(false);
      }, 500);
    }
  };

  // Inject responsive CSS for the selected device
  React.useEffect(() => {
    const currentDevice = devices.find(d => d.type === selectedDevice);
    if (!currentDevice) return;

    const styleId = 'preview-device-styles';
    let existingStyle = document.getElementById(styleId) as HTMLStyleElement;
    
    if (!existingStyle) {
      existingStyle = document.createElement('style');
      existingStyle.id = styleId;
      document.head.appendChild(existingStyle);
    }

    existingStyle.textContent = `
      .preview-container .container {
        max-width: ${currentDevice.width}px;
        margin: 0 auto;
        padding: 0 16px;
      }
      
      .preview-container {
        font-family: ${theme.fonts.body};
        line-height: 1.6;
        color: ${theme.colors.text};
        background: ${theme.colors.background};
      }
      
      .preview-container * {
        box-sizing: border-box;
      }
      
      .preview-container img {
        max-width: 100%;
        height: auto;
      }
      
      /* Responsive utilities */
      @media (max-width: 768px) {
        .preview-container .container {
          padding: 0 12px;
        }
      }
      
      /* Prevent interactions in preview */
      .preview-container a {
        pointer-events: none;
        cursor: default;
      }
      
      .preview-container button {
        pointer-events: none;
        cursor: default;
      }
      
      .preview-container form {
        pointer-events: none;
      }
    `;

    return () => {
      // Cleanup on unmount
      if (existingStyle && existingStyle.parentNode) {
        existingStyle.parentNode.removeChild(existingStyle);
      }
    };
  }, [selectedDevice, theme]);

  const currentDevice = devices.find(d => d.type === selectedDevice);

  if (!isOpen) return null;

  return (
    <PreviewContainer onClick={handleBackdropClick}>
      <PreviewBody>
        <DeviceFrame device={selectedDevice}>
          {currentDevice && (
            <DeviceInfo>
              {currentDevice.width}px
              {currentDevice.height && ` × ${currentDevice.height}px`}
            </DeviceInfo>
          )}
          
          <PreviewBadge>
            {currentDevice?.name} Preview
          </PreviewBadge>
          
          {isLoading && (
            <LoadingOverlay>
              Refreshing preview...
            </LoadingOverlay>
          )}
          
          <PreviewContent device={selectedDevice} className="preview-container">
            <BlocksContainer>
              {sortedBlocks.length === 0 ? (
                <div style={{ 
                  padding: '4rem 2rem', 
                  textAlign: 'center', 
                  color: '#6b7280',
                  fontSize: '16px' 
                }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#374151' }}>
                    No blocks added yet
                  </h3>
                  <p style={{ margin: 0 }}>
                    Add some blocks to your page to see the preview
                  </p>
                </div>
              ) : (
                sortedBlocks.map((pageBlock) => {
                  console.log('Rendering pageBlock in preview:', {
                    id: pageBlock.id,
                    blockId: pageBlock.blockId,
                    blockName: pageBlock.block.name,
                    isContainer: pageBlock.block.isContainer,
                    slots: pageBlock.block.slots,
                    overrides: pageBlock.overrides,
                    hasSlotData: !!pageBlock.slotData
                  });
                  
                  return (
                    <UIBlockRenderer
                      key={pageBlock.id}
                      block={pageBlock.block}
                      overrides={pageBlock.overrides}
                      slotData={pageBlock.slotData}
                      theme={theme}
                      isEditable={false}
                    />
                  );
                })
              )}
            </BlocksContainer>
          </PreviewContent>
        </DeviceFrame>
      </PreviewBody>
      
      <PreviewHeader>
        <PreviewTitle>{pageTitle} - Live Preview</PreviewTitle>
        <PreviewControls>
          <DeviceSelector>
            {devices.map((device) => (
              <DeviceButton
                key={device.type}
                active={selectedDevice === device.type}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeviceChange(device.type);
                }}
              >
                <span>{device.icon}</span>
                {device.name}
              </DeviceButton>
            ))}
          </DeviceSelector>
          <PreviewActions>
            <ActionButton onClick={(e) => {
              e.stopPropagation();
              handleRefresh();
            }}>
              🔄 Refresh
            </ActionButton>
            <CloseButton onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}>×</CloseButton>
          </PreviewActions>
        </PreviewControls>
      </PreviewHeader>
    </PreviewContainer>
  );
};